import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Camera, CheckCircle2, ClipboardCheck, Plus, RefreshCw, Search, ShieldCheck, TriangleAlert } from "lucide-react";
import {
  addWalkdownAsset, currentWalkdownRole, loadWalkdownSnapshot, submitWalkdownInspection, uploadWalkdownPhoto,
} from "./walkdownService";
import type { WalkdownAsset, WalkdownSnapshot, WalkdownVerification } from "./walkdownService";
import "./walkdownWorkspace.css";

const WRITE_ROLES = new Set(["technician", "supervisor", "maintenance_manager", "engineering", "administrator"]);

function prettyState(value: WalkdownVerification) {
  if (value === "verified") return "Verified";
  if (value === "needs_research") return "Needs Research";
  return "Incomplete";
}

export default function WalkdownWorkspace() {
  const [snapshot, setSnapshot] = useState<WalkdownSnapshot>({ lines: [], assets: [], latestInspectionByAsset: {} });
  const [lineId, setLineId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("viewer");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [verification, setVerification] = useState<WalkdownVerification>("incomplete");
  const [completeness, setCompleteness] = useState(0);
  const [result, setResult] = useState("");
  const [notes, setNotes] = useState("");
  const nameplateRef = useRef<HTMLInputElement>(null);
  const equipmentPhotoRef = useRef<HTMLInputElement>(null);

  async function reload(preferredAssetId?: string) {
    setBusy(true); setMessage("");
    try {
      const [next, nextRole] = await Promise.all([loadWalkdownSnapshot(), currentWalkdownRole()]);
      setSnapshot(next);
      setRole(nextRole);
      const nextLineId = lineId && next.lines.some((line) => line.id === lineId) ? lineId : next.lines[0]?.id ?? "";
      setLineId(nextLineId);
      const lineAssets = next.assets.filter((asset) => asset.line_id === nextLineId);
      const desired = preferredAssetId && lineAssets.some((asset) => asset.id === preferredAssetId) ? preferredAssetId : (assetId && lineAssets.some((asset) => asset.id === assetId) ? assetId : lineAssets[0]?.id ?? "");
      setAssetId(desired);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to load walkdown data");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  const line = snapshot.lines.find((item) => item.id === lineId);
  const lineAssets = useMemo(() => snapshot.assets
    .filter((asset) => asset.line_id === lineId)
    .sort((a, b) => (a.parent_asset_id ? 1 : 0) - (b.parent_asset_id ? 1 : 0) || a.display_order - b.display_order || a.asset_tag.localeCompare(b.asset_tag)), [snapshot.assets, lineId]);
  const filteredAssets = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return lineAssets;
    return lineAssets.filter((asset) => `${asset.asset_tag} ${asset.name} ${asset.equipment_type} ${asset.manufacturer ?? ""} ${asset.model ?? ""}`.toLowerCase().includes(needle));
  }, [lineAssets, query]);
  const asset = snapshot.assets.find((item) => item.id === assetId);
  const index = filteredAssets.findIndex((item) => item.id === assetId);
  const latestInspection = asset ? snapshot.latestInspectionByAsset[asset.id] : undefined;
  const canWrite = WRITE_ROLES.has(role);

  useEffect(() => {
    if (!asset) return;
    setManufacturer(asset.manufacturer ?? "");
    setModel(asset.model ?? "");
    setSerial(asset.serial_number ?? "");
    setVerification(asset.verification_state);
    setCompleteness(asset.completeness);
    setResult(latestInspection?.result ?? "");
    setNotes(latestInspection?.notes ?? "");
  }, [asset?.id]);

  function selectLine(nextLineId: string) {
    setLineId(nextLineId);
    const first = snapshot.assets.filter((item) => item.line_id === nextLineId).sort((a, b) => a.display_order - b.display_order)[0];
    setAssetId(first?.id ?? "");
    setQuery("");
  }

  function move(delta: number) {
    if (!filteredAssets.length) return;
    const nextIndex = Math.max(0, Math.min(filteredAssets.length - 1, (index < 0 ? 0 : index) + delta));
    setAssetId(filteredAssets[nextIndex].id);
  }

  async function saveWalkdown() {
    if (!asset || !canWrite) return;
    setBusy(true); setMessage("");
    try {
      await submitWalkdownInspection({
        assetId: asset.id,
        manufacturer,
        model,
        serialNumber: serial,
        verificationState: verification,
        completeness,
        result,
        notes,
        measurements: {
          source: "mobile_walkdown",
          asset_tag: asset.asset_tag,
          verification_state: verification,
          completeness,
        },
      });
      await reload(asset.id);
      setMessage(`${asset.asset_tag} walkdown saved.`);
      if (index >= 0 && index < filteredAssets.length - 1) setAssetId(filteredAssets[index + 1].id);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to save walkdown");
    } finally { setBusy(false); }
  }

  async function capturePhoto(file: File, kind: "nameplate_photo" | "equipment_photo") {
    if (!asset || !canWrite) return;
    setBusy(true); setMessage("");
    try {
      await uploadWalkdownPhoto(asset, file, kind);
      setMessage(`${kind === "nameplate_photo" ? "Nameplate" : "Equipment"} photo saved to ${asset.asset_tag}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to upload photo");
    } finally { setBusy(false); }
  }

  async function addMissing(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!line || !canWrite) return;
    const form = new FormData(event.currentTarget);
    const tag = String(form.get("tag") ?? "").trim();
    const name = String(form.get("name") ?? "").trim();
    const type = String(form.get("type") ?? "Generic").trim();
    if (!tag || !name) return;
    setBusy(true); setMessage("");
    try {
      const id = await addWalkdownAsset({
        lineId: line.id,
        parentAssetId: String(form.get("parent") ?? "") || null,
        assetTag: tag,
        name,
        equipmentType: type || "Generic",
        manufacturer: String(form.get("manufacturer") ?? ""),
        model: String(form.get("model") ?? ""),
        serialNumber: String(form.get("serial") ?? ""),
        criticality: String(form.get("criticality") ?? "B") as "A" | "B" | "C",
        notes: String(form.get("notes") ?? ""),
      });
      setShowAdd(false);
      await reload(id);
      setMessage(`${tag} added to ${line.name}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to add equipment");
    } finally { setBusy(false); }
  }

  return <div className="walkdown-workspace">
    <header className="ww-header">
      <div><div className="ww-eyebrow">MOBILE PLANT WALKDOWN</div><h1>Field Verification</h1><p>Walk the line, verify what is physically installed, photograph it, and close technical gaps.</p></div>
      <button className="ww-secondary" disabled={busy} onClick={() => void reload(assetId)}><RefreshCw size={17}/> Refresh</button>
    </header>

    <div className="ww-security"><ShieldCheck size={18}/><div><strong>{canWrite ? "Field writes enabled" : "Read-only role"}</strong><span>{role.replaceAll("_", " ")} · RLS + audited walkdown functions</span></div></div>

    <div className="ww-linebar">
      <label>Line<select value={lineId} onChange={(event) => selectLine(event.target.value)}>{snapshot.lines.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
      <div className="ww-line-progress"><div><span>Line documentation</span><strong>{line?.completeness ?? 0}%</strong></div><div className="ww-track"><span style={{ width: `${line?.completeness ?? 0}%` }}/></div></div>
      <button className="ww-primary" disabled={!canWrite || busy || !line} onClick={() => setShowAdd((value) => !value)}><Plus size={17}/> Missing Equipment</button>
    </div>

    {showAdd && <form className="ww-add" onSubmit={(event) => void addMissing(event)}>
      <div className="ww-section-title"><Plus size={17}/><div><strong>Add equipment discovered in the field</strong><span>Unknown is acceptable. Do not guess.</span></div></div>
      <div className="ww-form-grid">
        <label>Asset Tag<input name="tag" required placeholder="L01-EXT-001"/></label>
        <label>Equipment Name<input name="name" required placeholder="Extruder"/></label>
        <label>Type<input name="type" defaultValue="Generic"/></label>
        <label>Parent<select name="parent" defaultValue=""><option value="">Primary line equipment</option>{lineAssets.map((item) => <option key={item.id} value={item.id}>{item.asset_tag} · {item.name}</option>)}</select></label>
        <label>Manufacturer<input name="manufacturer" placeholder="Unknown"/></label>
        <label>Model<input name="model" placeholder="Unknown"/></label>
        <label>Serial<input name="serial" placeholder="Unknown"/></label>
        <label>Criticality<select name="criticality" defaultValue="B"><option>A</option><option>B</option><option>C</option></select></label>
        <label className="wide">Notes<textarea name="notes" rows={2}/></label>
      </div>
      <div className="ww-form-actions"><button type="button" className="ww-secondary" onClick={() => setShowAdd(false)}>Cancel</button><button className="ww-primary" disabled={busy}>Add to Line</button></div>
    </form>}

    {message && <div className="ww-message">{message}</div>}

    <div className="ww-layout">
      <aside className="ww-list">
        <div className="ww-search"><Search size={16}/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search this line..."/></div>
        <div className="ww-count">{filteredAssets.length} current assets</div>
        {filteredAssets.map((item) => <button key={item.id} className={`ww-list-item ${item.id === assetId ? "active" : ""}`} onClick={() => setAssetId(item.id)}><span className={`ww-dot ${item.verification_state}`}/><div><strong>{item.name}</strong><small>{item.asset_tag} · {prettyState(item.verification_state)} · {item.completeness}%</small></div></button>)}
        {!filteredAssets.length && <div className="ww-empty-small">No equipment documented on this line yet.</div>}
      </aside>

      <main className="ww-card">
        {!asset ? <div className="ww-empty"><ClipboardCheck size={36}/><h2>No asset selected</h2><p>Add the first equipment record or select another line.</p></div> : <>
          <div className="ww-card-head"><div><span className="ww-tag">{asset.asset_tag}</span><h2>{asset.name}</h2><p>{asset.equipment_type} · Criticality {asset.criticality}</p></div><div className={`ww-state ${asset.verification_state}`}>{prettyState(asset.verification_state)}</div></div>

          <div className="ww-step"><span>{index >= 0 ? index + 1 : 0} of {filteredAssets.length}</span>{latestInspection && <span>Last walkdown {new Date(latestInspection.inspected_at).toLocaleString()}</span>}</div>

          <div className="ww-verify-buttons">
            <button className={verification === "verified" ? "active verified" : ""} disabled={!canWrite || busy} onClick={() => setVerification("verified")}><CheckCircle2/> Verified</button>
            <button className={verification === "needs_research" ? "active research" : ""} disabled={!canWrite || busy} onClick={() => setVerification("needs_research")}><TriangleAlert/> Needs Research</button>
            <button className={verification === "incomplete" ? "active incomplete" : ""} disabled={!canWrite || busy} onClick={() => setVerification("incomplete")}><ClipboardCheck/> Incomplete</button>
          </div>

          <div className="ww-fields">
            <label>Manufacturer<input value={manufacturer} disabled={!canWrite} onChange={(event) => setManufacturer(event.target.value)} placeholder="Unknown"/></label>
            <label>Model<input value={model} disabled={!canWrite} onChange={(event) => setModel(event.target.value)} placeholder="Unknown"/></label>
            <label>Serial Number<input value={serial} disabled={!canWrite} onChange={(event) => setSerial(event.target.value)} placeholder="Unknown"/></label>
          </div>

          <div className="ww-complete"><div><span>Documentation completeness</span><strong>{completeness}%</strong></div><input type="range" min={0} max={100} step={5} disabled={!canWrite} value={completeness} onChange={(event) => setCompleteness(Number(event.target.value))}/><small>This should reflect how much of this equipment record is actually documented, not optimism.</small></div>

          <div className="ww-photos">
            <input ref={nameplateRef} type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void capturePhoto(file, "nameplate_photo"); event.currentTarget.value = ""; }}/>
            <input ref={equipmentPhotoRef} type="file" accept="image/*" capture="environment" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void capturePhoto(file, "equipment_photo"); event.currentTarget.value = ""; }}/>
            <button disabled={!canWrite || busy} onClick={() => nameplateRef.current?.click()}><Camera/> Nameplate Photo</button>
            <button disabled={!canWrite || busy} onClick={() => equipmentPhotoRef.current?.click()}><Camera/> Equipment Photo</button>
          </div>

          <label className="ww-long">Walkdown Result<input value={result} disabled={!canWrite} onChange={(event) => setResult(event.target.value)} placeholder="Verified in field, missing nameplate, requires OEM research..."/></label>
          <label className="ww-long">Field Notes<textarea rows={4} value={notes} disabled={!canWrite} onChange={(event) => setNotes(event.target.value)} placeholder="Condition, missing data, unusual configuration, measurement follow-up..."/></label>

          <div className="ww-bottom-actions"><button className="ww-secondary" disabled={index <= 0 || busy} onClick={() => move(-1)}><ArrowLeft/> Previous</button><button className="ww-primary ww-save" disabled={!canWrite || busy} onClick={() => void saveWalkdown()}><ClipboardCheck/> Save Walkdown</button><button className="ww-secondary" disabled={index < 0 || index >= filteredAssets.length - 1 || busy} onClick={() => move(1)}>Next <ArrowRight/></button></div>
        </>}
      </main>
    </div>
  </div>;
}
